import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { JobBoard } from "@/components/JobBoard";
import { AddJobDialog } from "@/components/AddJobDialog";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlusCircle, Link as LinkIcon, MessageSquarePlus, LogOut, CalendarDays } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/AuthContext";
import FocusMode from "@/components/FocusMode";
import { LanguageSwitcher } from './LanguageSwitcher';

const extractJobInfo = async (url: string) => {
  try {
    const response = await fetch(`https://api.microlink.io?url=${encodeURIComponent(url)}`);
    const data = await response.json();
    
    if (data.status === 'success') {
      const { title, description } = data.data;
      
      // Try to extract company name and job title from the page title
      let companyName = "New Company";
      let jobTitle = title || "New Position";

      // Common patterns in job listing titles
      const patterns = [
        /^(.*?)\s*(?:is hiring|hiring)\s*(?:a|an)?\s*(.*)$/i, // "Company is hiring a Position"
        /^(.*?)\s*[-|:]\s*(.*)$/i,                            // "Company - Position" or "Company: Position"
        /^(.*?)\s*?\((.*?)\)$/i,                             // "Position (Company)"
        /^(.*?)\s+at\s+(.*?)$/i,                             // "Position at Company"
      ];

      for (const pattern of patterns) {
        const match = title?.match(pattern);
        if (match) {
          // Different patterns might have company and position in different orders
          if (pattern.toString().includes('at')) {
            [, jobTitle, companyName] = match;
          } else {
            [, companyName, jobTitle] = match;
          }
          break;
        }
      }

      return {
        companyName: companyName.trim(),
        jobTitle: jobTitle.trim(),
        description: description || ""
      };
    }
  } catch (error) {
    console.error('Error fetching job info:', error);
  }

  return {
    companyName: "New Company",
    jobTitle: "New Position",
    description: ""
  };
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [quickAddUrl, setQuickAddUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const jobBoardRef = useRef<{ fetchJobs: () => void }>();
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showFocusMode, setShowFocusMode] = useState(false);

  const handleQuickAdd = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && quickAddUrl.trim()) {
      setIsLoading(true);
      
      try {
        const jobInfo = await extractJobInfo(quickAddUrl);
        
        const { error } = await supabase
          .from('jobs')
          .insert({
            url: quickAddUrl,
            company_name: jobInfo.companyName,
            job_title: jobInfo.jobTitle,
            description: jobInfo.description,
            status: "Interested",
            date_added: new Date().toISOString(),
            user_id: user?.id
          });

        if (error) {
          toast({
            title: t('dashboard.jobAddError'),
            description: error.message,
            variant: "destructive",
          });
          return;
        }

        toast({
          title: t('dashboard.jobAdded'),
          description: t('dashboard.jobAdded'),
        });

        setQuickAddUrl("");
        jobBoardRef.current?.fetchJobs();
      } catch (error) {
        toast({
          title: t('dashboard.jobAddError'),
          description: t('dashboard.jobAddError'),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const toggleFocusMode = () => {
    console.log('Toggling focus mode, current state:', showFocusMode);
    setShowFocusMode(!showFocusMode);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('common.error'),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#2C2E2D] text-[#D9D9D9]">
      {/* Header Section */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b shadow-sm bg-[#2C2E2D]/95 backdrop-blur-sm border-[#818881]">
        <div className="max-w-7xl mx-auto p-4">
          {/* Logo - always visible on desktop and mobile */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="w-full flex justify-between items-center">
              <Link to="/" className="hover:opacity-90 transition-opacity">
                <img 
                  src="/JobPin_logo_2edt_white.svg"
                  alt="JobPin Logo" 
                  className="h-8 md:h-12 w-auto"
                />
              </Link>
              
              {/* Mobile-only buttons */}
              <div className="flex md:hidden items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFeedbackOpen(true)}
                  className="w-10 h-10 p-0 flex items-center justify-center bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                >
                  <MessageSquarePlus className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSignOut}
                  className="w-10 h-10 p-0 flex items-center justify-center bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            {/* URL input field - full width on mobile */}
            <div className="w-full md:max-w-xl">
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('dashboard.searchPlaceholder')}
                  value={quickAddUrl}
                  onChange={(e) => setQuickAddUrl(e.target.value)}
                  onKeyDown={handleQuickAdd}
                  className={cn(
                    "pl-10 pr-4 w-full py-2.5",
                    "bg-[#818881]/20 border-[#818881] text-[#D9D9D9] placeholder:text-[#818881]",
                    isLoading && "opacity-50 cursor-wait"
                  )}
                  disabled={isLoading}
                />
                {isLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                  </div>
                )}
              </div>
            </div>
            
            {/* Desktop-only buttons */}
            <div className="hidden md:flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFeedbackOpen(true)}
                className="min-w-[100px] bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
              >
                <MessageSquarePlus className="h-4 w-4 mr-2" />
                {t('dashboard.feedback')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="min-w-[100px] bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('dashboard.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24">
        <div className="max-w-7xl mx-auto p-4">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold">{t('dashboard.title')}</h1>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <Button 
                onClick={toggleFocusMode}
                variant="outline"
                className="min-w-[120px] bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751]/90 hover:text-[#2C2E2D] hover:border-[#CCF751]"
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                {t('dashboard.focusMode')}
              </Button>
              <Button 
                onClick={() => setOpen(true)}
                className="bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                {t('dashboard.addJob')}
              </Button>
            </div>
          </div>
          
          {showFocusMode ? (
            <FocusMode 
              isDarkMode={true} 
              onBackToDashboard={() => setShowFocusMode(false)} 
              demoMode={false}
            />
          ) : (
            <JobBoard ref={jobBoardRef} isDarkMode={true} />
          )}
        </div>
      </main>

      <AddJobDialog 
        open={open} 
        onOpenChange={setOpen}
        onJobAdded={() => {
          jobBoardRef.current?.fetchJobs();
        }}
        isDarkMode={true}
      />

      <FeedbackDialog
        open={isFeedbackOpen}
        onOpenChange={setIsFeedbackOpen}
        isDarkMode={true}
      />
    </div>
  );
};

export default Dashboard; 